import {createUpdateConversationsTrackerStatusTask} from '../../../../srcCommon/tasks/conversations/updateConversationsTrackerStatusTask';
import {
  buildConversationUpdater,
  bumpConversationTracker,
  BumpedAtFinder,
  ConversationActionContextFinder,
  ConversationStatusesEnum,
  setConversationStatus,
  statusToTrackerStatus,
} from '../../../helpers/models/conversationHelpers';
import {
  buildDestroyConversationPatch,
  buildSetStatusConversationPatch,
  shouldUseTeamPatch,
} from '../../../helpers/models/conversationPatchHelpers';
import {ConversationActionContextsEnum} from '../../../models/api/conversationCommonTypes';
import {StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {updateConversationsBulkAction} from './bulkConversationsActions';
import {clearHiddenConversationIdAction} from './conversationsActions';
import {updateConversationsAction} from './updateConversationsActions';

interface UndoOptions {
  preventUndo?: boolean;
  findBumpedAt?: BumpedAtFinder;
}

export function setConversationsStatusAction(
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  newStatus: ConversationStatusesEnum,
  findConversationActionContext: ConversationActionContextFinder,
  undoOptions: UndoOptions = {},
): ThunkAsyncAction {
  const {findBumpedAt} = undoOptions;

  return async (dispatch, getState) => {
    const {teammateId} = cellsContext;

    const offlineTask = maybeCreateOfflineTask(
      getState,
      cellsContext,
      conversationIds,
      newStatus,
      undoOptions.findBumpedAt,
    );

    const options = {
      cellsContext,
      offlineTask,
      undo: undoOptions.preventUndo
        ? undefined
        : {
            type: ConversationUndoBannerTypesEnum.STATUS,
          },
    };

    // If we're acting on one conversation, let's remove the hidden conversation.
    if (conversationIds.length === 1) {
      dispatch(clearHiddenConversationIdAction());
    }

    await dispatch(
      updateConversationsAction(
        conversationIds,
        (conversationId) => {
          // We don't care about the conversation's action context if we're destroying it.
          if (newStatus === ConversationStatusesEnum.DESTROYED) {
            return buildDestroyConversationPatch(teammateId);
          }

          const conversationActionContext = findConversationActionContext(conversationId);
          return (
            conversationActionContext &&
            buildSetStatusConversationPatch(
              teammateId,
              conversationActionContext,
              newStatus,
              findBumpedAt && findBumpedAt(conversationId),
            )
          );
        },
        buildConversationUpdater(
          // Bump the conversation for this teammate's tracker.
          bumpConversationTracker(cellsContext.teammateId, {findBumpedAt}),
          // Set the status.
          setConversationStatus(cellsContext, newStatus),
        ),
        options,
      ),
    );
  };
}

export function setBulkConversationsStatusAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  newStatus: ConversationStatusesEnum,
): ThunkAsyncAction {
  const conversationPatch = buildSetStatusConversationPatch(
    cellsContext.teammateId,
    shouldUseTeamPatch(cellsContext)
      ? ConversationActionContextsEnum.TEAM
      : ConversationActionContextsEnum.TEAMMATE,
    newStatus,
  );
  return updateConversationsBulkAction(inboxesParameters, cellsContext, conversationPatch);
}

function maybeCreateOfflineTask(
  getState: StateGetter,
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  status: ConversationStatusesEnum,
  findBumpedAt?: BumpedAtFinder,
) {
  return createUpdateConversationsTrackerStatusTask(getState, cellsContext, conversationIds, {
    status: statusToTrackerStatus(status),
    findBumpedAt,
  });
}
